import React, { useCallback, useEffect, useState } from 'react';
import { getLogs, getLogDetails, deleteLog } from '../../services/LogServices';
import SearchBar from '../common/SearchBar';
import Pagination from '../common/Pagination';
import styles from './styles/Logs.module.css';
import { AiOutlineClose } from 'react-icons/ai';
import { FaDownload, FaPrint, FaFilePdf, FaSpinner } from 'react-icons/fa';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Collapse an ordered decision list into one row per situation *visit*.
// Consecutive decisions sharing a situation merge into a single row, and the
// choice column lists every choice/input made there (self-referential hint clicks
// included). Non-consecutive revisits (A -> B -> A) stay as separate rows. This
// also drops the "Unknown Choice" noise, since arrival ('situation') decisions are
// visit markers that contribute nothing to the choice column.
const groupDecisionsBySituation = (decisions = []) => {
    const groups = [];

    for (const d of decisions) {
        const key = d.situationId?._id ? String(d.situationId._id) : (d.situationTitle || 'unknown');
        const prev = groups[groups.length - 1];

        if (!prev || prev.key !== key) {
            groups.push({
                key,
                situationTitle: d.situationTitle || 'Unknown Situation',
                timestamp: d.timestamp,
                choices: [],
            });
        }

        if (d.type === 'choice' || d.type === 'input') {
            groups[groups.length - 1].choices.push({
                display: d.choiceDisplay,
                type: d.type,
                correctness: d.correctness,
            });
        }
    }

    return groups;
};

const Logs = ({ isMobile, userId }) => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [logsPerPage] = useState(10);

    const [loadingDetails, setLoadingDetails] = useState({});

    const [expandedLogs, setExpandedLogs] = useState({});

    const fetchLogs = useCallback(() => {
        setLoading(true);
        setError(null);

        getLogs({ page: currentPage, limit: logsPerPage, search: searchTerm })
            .then((data) => {
                setLogs(data.logs || []);
                setTotal(data.total || 0);
                setTotalPages(data.totalPages || 0);
                setLoading(false);
            })
            .catch(() => {
                setError('Error while getting logs');
                setLoading(false);
            });
    }, [currentPage, logsPerPage, searchTerm]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs, userId]);

    // Receives the already-debounced term from the shared SearchBar (filterOnly
    // mode); must stay referentially stable since SearchBar closes over it.
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term || '');
        setCurrentPage(1);
    }, []);

    const fetchLogDetails = async (logId) => {
        const existingLog = logs.find(l => l._id === logId);
        if (existingLog && existingLog.formattedDecisions) {
            return existingLog;
        }

        setLoadingDetails(prev => ({ ...prev, [logId]: true }));

        try {
            const detailedLog = await getLogDetails(logId);
            console.log("LOG DETAILS: ", detailedLog);

            setLogs(prevLogs => 
                prevLogs.map(log => log._id === logId ? detailedLog : log)
            );
            
            setLoadingDetails(prev => ({ ...prev, [logId]: false }));
            return detailedLog;
        } catch (err) {
            console.error("Failed to fetch log details", err);
            setLoadingDetails(prev => ({ ...prev, [logId]: false }));
            return null;
        }
    };

    const handleDeleteLog = (event, logId) => {
        event.stopPropagation();
        if(!window.confirm("Are you sure you want to delete this log?")) return;

        deleteLog(logId)
            .then(() => {
                // Refetch so pagination stays consistent; step back a page if
                // this delete emptied the current one.
                if (logs.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else {
                    fetchLogs();
                }
            })
            .catch(() => {
                setError('Error while deleting log');
            });
    };

    const toggleExpandLog = async (logId) => {
        if (expandedLogs[logId]) {
            setExpandedLogs(prev => ({ ...prev, [logId]: false }));
            return;
        }

        await fetchLogDetails(logId);
        setExpandedLogs(prev => ({ ...prev, [logId]: true }));
    };


    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };


    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    const prepareLogForExport = async (log) => {
        if (log.formattedDecisions) return log;
        return await fetchLogDetails(log._id);
    };

    const handleDownloadPDF = async (partialLog) => {
        const log = await prepareLogForExport(partialLog);
        if (!log) return;

        const doc = new jsPDF();
        const margin = 14;
        const u = (log?.viewer?.username || 'user');
        const s = (log?.scenario?.title || 'scenario');
        
        doc.setFontSize(18);
        doc.setTextColor(99, 102, 241);
        doc.text("Scenario Log", margin, 22);

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`User: ${u}`, margin, 30);
        doc.text(`Scenario: ${s}`, margin, 36);
        doc.text(`Status: ${log?.status}`, margin, 42);
        doc.text(`Exported: ${new Date().toLocaleString()}`, margin, 48);

        const rows = groupDecisionsBySituation(log.formattedDecisions).map((g) => [
            formatDate(g.timestamp),
            g.situationTitle || '',
            g.choices.length === 0
                ? '—'
                : g.choices.map(c => c.type === 'input' ? `"${c.display}"` : c.display).join('\n')
        ]);

        autoTable(doc, {
            head: [['Time', 'Situation', 'Decision / Input']],
            body: rows,
            startY: 60,
            margin: { left: margin, right: margin },
            headStyles: { fillColor: [99, 102, 241] },
        });

        doc.save(`${u}_${s}_log.pdf`);
    };

    const handlePrint = async (partialLog) => {
        const log = await prepareLogForExport(partialLog);
        if (!log) return;

        const w = window.open('', '_blank');
        if (!w) return;

        const u = log?.viewer?.username || 'Unknown';
        const s = log?.scenario?.title || 'Unknown';

        const rowsHtml = groupDecisionsBySituation(log.formattedDecisions)
            .map(g => {
                const choiceHtml = g.choices.length === 0
                    ? '—'
                    : g.choices.map(c => c.type === 'input' ? `<em>"${c.display}"</em>` : c.display).join('<br/>');
                return `
                <tr>
                    <td>${formatDate(g.timestamp)}</td>
                    <td>${g.situationTitle || ''}</td>
                    <td>${choiceHtml}</td>
                </tr>
            `;
            }).join('');

        w.document.write(`
            <html>
                <head>
                    <title>Log: ${u}</title>
                    <style>
                        body { font-family: sans-serif; padding: 20px; }
                        h1 { color: #6366f1; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #6366f1; color: white; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                    </style>
                </head>
                <body>
                    <h1>Scenario Log</h1>
                    <p><strong>User:</strong> ${u} | <strong>Scenario:</strong> ${s}</p>
                    <table>
                        <thead><tr><th>Time</th><th>Situation</th><th>Decision</th></tr></thead>
                        <tbody>${rowsHtml}</tbody>
                    </table>
                </body>
            </html>
        `);
        w.document.close();
        w.onload = () => { w.print(); w.close(); };
    };

    const handleDownloadJSON = async (partialLog) => {
        const log = await prepareLogForExport(partialLog);
        if (!log) return;

        const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${log._id}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <div className={styles.logsContainer}>
            <div className={styles.headerBar}>
                <div className={styles.searchBar}>
                    <SearchBar type="logs" setter={handleSearchChange} />
                </div>
                <div className={styles.headerChips}>
                    <span className={styles.chip}>Total: {total}</span>
                    <span className={styles.chip}>Page: {currentPage}</span>
                </div>
            </div>

            {loading && <p className={styles.loading}>Loading logs...</p>}
            {error && <p className={styles.error}>{error}</p>}
            
            {logs.map((log) => {
                const user = log?.viewer?.username || log?.anonymousId || 'Anonymous';
                const scenario = log?.scenario?.title || 'Unknown Scenario';
                const isComplete = log?.status === 'completed';
                const isLoadingThis = loadingDetails[log._id];

                return (
                    <div
                        key={log._id}
                        className={isMobile ? styles.logMobile : styles.log}
                        onClick={() => toggleExpandLog(log._id)}
                    >
                        <div className={styles.logHeader}>
                            <h3 className={styles.logTitle}>
                                <span>{user}</span>
                                <span>•</span>
                                <span>{scenario}</span>
                            </h3>
                            {isLoadingThis && <FaSpinner className={styles.spinner} />}
                        </div>

                        <div className={styles.meta}>
                            <span className={`${styles.badge} ${isComplete ? styles.ok : styles.warn}`}>
                                {log?.status === 'in_progress' ? 'In Progress' : log.status}
                            </span>
                            <span className={styles.badge}>
                                {formatDate(log?.updatedAt)}
                            </span>
                        </div>

                        <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                            <button className={styles.iconBtn} onClick={() => handleDownloadJSON(log)}>
                                <FaDownload />
                            </button>
                            <button className={styles.iconBtn} onClick={() => handleDownloadPDF(log)}>
                                <FaFilePdf />
                            </button>
                            <button className={styles.iconBtn} onClick={() => handlePrint(log)}>
                                <FaPrint />
                            </button>
                            <button className={`${styles.iconBtn} ${styles.danger}`} onClick={(e) => handleDeleteLog(e, log._id)}>
                                <AiOutlineClose />
                            </button>
                        </div>

                        {expandedLogs[log._id] && (
                            <div className={styles.tableWrapper} onClick={(e) => e.stopPropagation()}>
                                {log.formattedDecisions ? (
                                    <div className={styles.tableScroller}>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Situation</th>
                                                    <th>Choice / Input</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupDecisionsBySituation(log.formattedDecisions).map((g, i) => (
                                                    <tr key={i}>
                                                        <td>{formatDate(g.timestamp)}</td>
                                                        <td>{g.situationTitle}</td>
                                                        <td>
                                                            {g.choices.length === 0
                                                                ? '—'
                                                                : g.choices.map((c, j) => (
                                                                    <div key={j}>
                                                                        {c.type === 'input'
                                                                            ? <span style={{fontStyle:'italic'}}>"{c.display}"</span>
                                                                            : c.display
                                                                        }
                                                                    </div>
                                                                ))
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {/* <div className={styles.toolsBar}>
                                             <small>Score: {log.score} | Time: {log.timeSpentSeconds}s</small>
                                        </div> */}
                                    </div>
                                ) : (
                                    <div className={styles.loading}>Loading details...</div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={paginate}
            />
        </div>
    );
};

export default Logs;